import * as React from 'react';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Skeleton } from '../../../components/ui/skeleton';
import { cn } from '../../../lib/utils';

export type TabVariant = 'pill' | 'underline';

export interface PhaseSubTabsProps<K extends string> {
  /** Current selection. */
  active: K;
  /**
   * Tab definitions in display order. `count` accepts a `'loading'` sentinel
   * (Task 16 — public read-only share view) for a SINGLE tab whose count
   * cannot be resolved (e.g. every un-granted pill on a public share link) —
   * renders a small skeleton chip beside the label instead of a number, so
   * it never claims a false `0`. This is distinct from the whole-row
   * `loading` prop below, which skeletons every tab uniformly; the sentinel
   * lets one tab (e.g. the granted pill) show its real label + count while
   * its siblings stay unresolved.
   */
  tabs: ReadonlyArray<{ key: K; label: string; count?: number | 'loading'; panelId?: string }>;
  /** Called with the new selection. */
  onChange: (next: K) => void;
  /** Optional extra classes for the wrapping row. */
  className?: string;
  /**
   * Show shimmer skeleton pills instead of real tabs.
   * Use while data is loading so counts don't flash through 0.
   */
  loading?: boolean;
  /**
   * Skip the border-b card-header wrapper and render just the pill row.
   * Use when embedding tabs inside a page header or toolbar rather than
   * at the top of a card. (Ignored for the `underline` variant.)
   */
  bare?: boolean;
  /**
   * Visual style. `pill` (default) = segmented control (Scripts/Content/
   * Creators); `underline` = Figma settings-style underline tab bar.
   */
  variant?: TabVariant;
  /** Accessible name for the tab list. */
  ariaLabel?: string;
}

/**
 * The single app-facing tabs component — a styled preset over the shadcn
 * `Tabs` primitive (`components/ui/tabs`). Supports two variants:
 *  - `pill`: segmented control, active tab = white `bg-card` pill.
 *  - `underline`: Figma underline tab bar, active tab = foreground underline.
 *
 * It is the controlled tab BAR only (content is rendered by the caller based on
 * `active`), with optional count badges and a loading skeleton.
 */
const LIST_CLASS: Record<TabVariant, string> = {
  // Figma: standalone rounded-full pills (not a segmented control), active = black.
  pill: 'h-auto w-fit gap-[10px] bg-transparent p-0',
  underline: 'h-auto w-full justify-start gap-8 rounded-none border-b border-border bg-transparent p-0',
};

const TRIGGER_CLASS: Record<TabVariant, string> = {
  // Figma 3072:75554 "Tab Button": h-[36px], active = black bg + #fafafa text
  // (foreground/primary/default, not pure white), inactive = stone-200 bg +
  // muted text.
  pill: cn(
    'flex h-[36px] items-center rounded-full px-4 text-body-sm-medium tracking-[-0.1px] transition-colors border-0',
    'bg-stone-200 text-muted-foreground hover:text-foreground',
    'data-[state=active]:bg-foreground-ink data-[state=active]:text-foreground-primary-default data-[state=active]:shadow-none',
  ),
  // Active underline is a 2px rounded black bar drawn as an ::after element
  // (not border-b) so it can be rounded — matching UnderlineTabNav and the
  // review tab bars.
  underline: cn(
    'relative flex-none rounded-none border-0 bg-transparent px-0 pb-3 shadow-none',
    'text-body-sm-medium text-muted-foreground transition-colors hover:text-foreground',
    "after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-[2px] after:bg-transparent after:content-['']",
    'data-[state=active]:bg-transparent data-[state=active]:text-foreground-ink data-[state=active]:shadow-none data-[state=active]:after:bg-black',
  ),
};

function TabsRow<K extends string>({
  active,
  tabs,
  onChange,
  variant,
  ariaLabel,
}: Pick<PhaseSubTabsProps<K>, 'active' | 'tabs' | 'onChange' | 'ariaLabel'> & { variant: TabVariant }): React.ReactElement {
  return (
    <Tabs value={active} onValueChange={(v) => onChange(v as K)} className={variant === 'underline' ? 'w-full' : undefined}>
      <TabsList aria-label={ariaLabel} className={LIST_CLASS[variant]}>
        {tabs.map((t) => (
          <TabsTrigger
            key={t.key}
            value={t.key}
            {...(t.panelId ? { id: `${t.panelId}-trigger`, 'aria-controls': t.panelId } : {})}
            className={TRIGGER_CLASS[variant]}
            // Exempt from the public share view's click interception — this
            // is the pill CONTROL itself (not a container), so it's always
            // allowed under `PublicCampaignShell.tsx`'s `isAllowedClick`
            // rules; outside that shell the attribute is inert (no
            // interceptor reads it). Static/unconditional here rather than
            // gated on public mode: every caller (Creators/Scripts/Content/
            // Confirmed/Live-content/Settings) already no-ops its own
            // `onChange` in public mode unless it's specifically wired to
            // prompt sign-in (see CreatorsTab.tsx's status pills), so letting
            // the click physically reach that handler doesn't change
            // behaviour for the callers that haven't opted in yet.
            data-public-allowed=""
          >
            {/* Figma renders "{label} {count}" as one uniform text run — no
                dimmed/smaller count badge. The 'loading' sentinel is the one
                exception: a small skeleton chip stands in for the number so
                an unresolved count never reads as a false "0". */}
            {typeof t.count === 'number' ? (
              `${t.label} ${t.count}`
            ) : t.count === 'loading' ? (
              <span className="inline-flex items-center gap-1.5">
                {t.label}
                <Skeleton className="h-3 w-4 rounded-sm" />
              </span>
            ) : (
              t.label
            )}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

export function PhaseSubTabs<K extends string>({
  active,
  tabs,
  onChange,
  className,
  loading,
  bare,
  variant = 'pill',
  ariaLabel,
}: PhaseSubTabsProps<K>): React.ReactElement {
  if (loading) {
    // Match each variant's real tab shape exactly — pill: h-[36px] rounded-full
    // (TRIGGER_CLASS.pill); underline: a plain label-width bar sitting on the
    // same border-b the real underline bar uses, no pill chrome.
    if (variant === 'underline') {
      return (
        <div className={cn('flex w-full items-end gap-8 border-b border-border pb-3', className)}>
          {[72, 96, 64, 84].slice(0, tabs.length || 3).map((w, i) => (
            <Skeleton key={i} className="h-4" style={{ width: w }} />
          ))}
        </div>
      );
    }
    return (
      <div className={cn('flex flex-wrap items-center gap-[10px]', bare ? className : 'border-b border-border bg-card px-3 py-2', className)}>
        {[72, 64, 48, 56].slice(0, tabs.length || 3).map((w, i) => (
          <Skeleton key={i} className="h-[36px] rounded-full" style={{ width: w }} />
        ))}
      </div>
    );
  }

  const row = <TabsRow active={active} tabs={tabs} onChange={onChange} variant={variant} ariaLabel={ariaLabel} />;

  // Underline = full-width bar with its own border-b (no extra card wrapper).
  if (variant === 'underline') return <div className={className}>{row}</div>;

  if (bare) {
    return <div className={cn('flex flex-wrap items-center gap-1', className)}>{row}</div>;
  }

  return <div className={cn('border-b border-border bg-card px-3 py-2', className)}>{row}</div>;
}
